import React, { type ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { PageContainer } from "@packsize/components";
import { Box, Grid, type SxProps, Tab, Tabs, Typography } from "@mui/material";

interface ConfigurationPageLayoutTabWithValue {
	label: string;
	value: string;
}

interface ConfigurationPageLayoutTabWithLink {
	label: string;
	link: string;
}

export type ConfigurationPageLayoutTab = ConfigurationPageLayoutTabWithValue | ConfigurationPageLayoutTabWithLink;

export interface ConfigurationPageLayoutProps {
	readonly title: string;
	readonly titleActions?: ReactNode;
	readonly actions?: ReactNode;
	readonly children: ReactNode;
	readonly tabs?: ConfigurationPageLayoutTab[];
	readonly activeTab?: string;
	readonly onTabChange?: (value: string) => void;
	readonly sx?: SxProps;
	readonly contentSx?: SxProps;
	readonly pendoId?: string;
}

const isLinkTab = (tab: ConfigurationPageLayoutTab): tab is ConfigurationPageLayoutTabWithLink => {
	return "link" in tab;
};

const defaultTabs: ConfigurationPageLayoutTab[] = [];

export const ConfigurationPageLayout: React.FC<ConfigurationPageLayoutProps> = ({
	title,
	titleActions,
	actions,
	children,
	tabs = defaultTabs,
	activeTab,
	onTabChange,
	sx,
	contentSx,
	pendoId,
}) => {
	const location = useLocation();

	const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
		onTabChange?.(newValue);
	};

	const resolvedActiveTab =
		activeTab ?? tabs.filter(isLinkTab).find((tab) => location.pathname.endsWith("/" + tab.link))?.link;

	return (
		<PageContainer sx={sx} data-pendoid={pendoId}>
			<Grid
				container
				display="grid"
				gridTemplateRows="min-content 1fr"
				gap={2}
				sx={{ flex: 1, overflow: "hidden", ...contentSx }}
			>
				<Grid container justifyContent="space-between" alignItems="center" gap={2}>
					<Box display="flex" alignItems="flex-end" gap={2}>
						<Typography variant="h1">{title}</Typography>
						{titleActions}

						<Tabs value={resolvedActiveTab} onChange={handleTabChange}>
							{tabs.map((tab) => {
								if (isLinkTab(tab)) {
									return (
										<Tab
											key={tab.link}
											label={tab.label}
											value={tab.link}
											component={Link}
											to={tab.link}
											data-pendoid={`configuration-page__tab__${tab.link}`}
										/>
									);
								}

								return (
									<Tab
										key={tab.value}
										label={tab.label}
										value={tab.value}
										data-pendoid={`configuration-page__tab__${tab.value}`}
									/>
								);
							})}
						</Tabs>
					</Box>

					{Boolean(actions) === true && (
						<Box display="flex" gap={1}>
							{actions}
						</Box>
					)}
				</Grid>

				{children}
			</Grid>
		</PageContainer>
	);
};
